import type { QueryHookOptions } from '@apollo/client/react'
import type { ListActionResult } from '../types/QueryInput'
import { HydrationStatusEnum } from '../types/QueryInput'
import type { UseListProps } from './index'
import { requiredCheck } from '@ztechapps/common.utils'
import merge from 'lodash/merge'
import isEmpty from 'lodash/isEmpty'
import { buildQueryVariables } from './buildQueryVariables'
import { useListAction } from '../useListAction'

export type UseBuildQueryOptionsResult<T = any> = {
  dbOptions: Record<any, any>
  queryOptions: QueryHookOptions<T, any>
  listActionResults: ListActionResult
}
export const useBuildListQueryOptions = <T = any>({
  query,
  entity,
  projection,
  sort,
  limit,
  skip,
  filter,
  options = {},
  listActionOptions = {},
}: UseListProps): UseBuildQueryOptionsResult<T> => {
  requiredCheck(query, 'useBuildListOptions.query')
  requiredCheck(entity, 'useBuildListOptions.entity')

  let { queryOptions = {} } = options
  const dbOptions = options.dbOptions ?? {}
  limit = limit ?? 100

  //merge the sort and filter to the initial values
  listActionOptions = merge({}, listActionOptions, {
    defaultSort: sort,
    defaultSearch: filter,
  })

  //use from the injection that listAction that will be used
  const listActionResults = useListAction(listActionOptions)

  //the result of list actions will be used here, because what was default will be merged automatically
  sort = listActionResults?.sort
  filter = listActionResults?.filter

  //when using custom listAction, we skip it if the filter,sort and other params are not yet loaded from storage
  if (!isEmpty(listActionOptions?.storageKey) && listActionResults.hydrationStatus === HydrationStatusEnum.hydrating) {
    queryOptions.skip = true
  }

  //because the filter and options are part of what apollo tracks, we need to have a consistent way of
  //stringify that, else it will query and do extra call, and this buildQueryVariables handles that
  const variables = buildQueryVariables(skip, filter, { sort, projection }, limit, dbOptions)

  //use lodash merge so we can overwrite values to the variables
  queryOptions = merge(
    {
      variables,
      notifyOnNetworkStatusChange: true,

      //this will use the current cache, unless was refresh.
      //fetchPolicy: 'cache-first',
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-only',
    },
    queryOptions
  )
  return { queryOptions, listActionResults, dbOptions }
}
